import { createAction as createReduxAction } from "@reduxjs/toolkit"
import { Socket } from "socket.io-client"
import { isLocalDevelopmentEnvironment } from "tools/debugging/module"

type HasRequiredKeys<T> = {
    [K in keyof T]-?: object extends Pick<T, K> ? never : K
}[keyof T]
type OnlyOptional<T> = HasRequiredKeys<T> extends never ? true : false

export const generateGenericCases = <
    T,
    C extends readonly (keyof T)[],
    S extends C[number][],
    U extends readonly (keyof T)[],
    CTX extends Record<string, unknown> | void = void,
    ServerCTX extends Record<string, unknown> | void = CTX,
>(
    entity: string
) => {
    type ClientCreateArgs = Pick<T, S[number]>
    type ServerCreateArgs = Pick<T, C[number]>
    type UpdateArgs = Partial<Pick<T, U[number]>>
    const createAction = createReduxAction<
        (S extends never[]
            ? ClientCreateArgs
            : {
                  data: ClientCreateArgs
              }) &
            (CTX extends void
                ? unknown
                : OnlyOptional<CTX> extends true
                  ? { context?: CTX }
                  : { context: CTX })
    >(`create${entity}`)
    const updateAction = createReduxAction<
        {
            id: string
            updates: UpdateArgs
        } & (CTX extends void
            ? unknown
            : OnlyOptional<CTX> extends true
              ? { context?: CTX }
              : { context: CTX })
    >(`update${entity}`)
    const deleteAction = createReduxAction<
        {
            id: string
        } & (CTX extends void
            ? unknown
            : OnlyOptional<CTX> extends true
              ? { context?: CTX }
              : { context: CTX })
    >(`delete${entity}`)
    return {
        createAction,
        updateAction,
        deleteAction,
        create: (socket: Socket, args: ServerCreateArgs, context: ServerCTX) => {
            socket.emit("create" + entity, args, context)
        },
        update: (socket: Socket, id: string, args: UpdateArgs, context: ServerCTX) => {
            if (Object.keys(args).length || (context && Object.keys(context).length))
                socket.emit("update" + entity, id, args, context)
            else if (isLocalDevelopmentEnvironment()) console.warn("Called update with no updates")
        },
        delete: (socket: Socket, id: string, context: ServerCTX) => {
            socket.emit("delete" + entity, id, context)
        },
    }
}
